import { acceptHMRUpdate, defineStore } from "pinia";
import type { Account } from "@nwt/norce-pwa/dist/runtime/types/api/account/account";
import type { Basket } from "@nwt/norce-pwa/dist/runtime/types/api/shopping/basket";

interface State {
	companyId: number | null;
}

export const useSelectedCompanyStore = defineStore({
	id: "selectedCompany",
	state: (): State => ({
		companyId: null
	}),
	getters: {
		isPersonal: (state) => state.companyId === null
	},
	actions: {
		selectPersonal() {
			this.companyId = null;
		},
		selectCompany(user: Account | null, companyId: number) {
			const exists = user?.Companies?.some((c) => c.Id === companyId);
			if (!exists) {
				this.selectPersonal();
				return false;
			}
			this.companyId = companyId;
			return true;
		},
		// Keep the persisted selection consistent with the current user. If `user` is
		// null we leave `companyId` untouched — null user can mean "not yet hydrated"
		// and we don't want to clobber the persisted selection during page reloads.
		// Explicit logout should call `reset()` directly.
		syncWithUser(user: Account | null) {
			if (!user) return;
			if (this.companyId == null) return;
			const stillValid = user.Companies?.some((c) => c.Id === this.companyId);
			if (!stillValid) {
				this.selectPersonal();
			}
		},
		// Source-of-truth sync from the active basket. Norce stamps `Basket.CompanyId`
		// when the basket was created in a company context, so on reload we trust the
		// basket over any (possibly stale or missing) localStorage state.
		syncFromBasket(basket: Basket | null | undefined, user: Account | null) {
			const basketCompanyId = basket?.CompanyId ?? null;
			if (basketCompanyId == null) {
				// Basket has no company — clear personal/company selection.
				this.selectPersonal();
				return;
			}
			// Only accept the basket's CompanyId if the user actually has access to it.
			// Guards against stale baskets carrying a company the user no longer belongs to.
			const exists = user?.Companies?.some((c) => c.Id === basketCompanyId);
			if (exists) {
				this.companyId = basketCompanyId;
			} else {
				this.selectPersonal();
			}
		},
		reset() {
			this.selectPersonal();
		}
	},
	persist: [{ storage: persistedState.localStorage, pick: ["companyId"] }]
});

if (import.meta.hot) {
	import.meta.hot.accept(
		acceptHMRUpdate(useSelectedCompanyStore, import.meta.hot)
	);
}
